/**
 * Adding photographs from the browser.
 *
 * ## The hash is not an implementation detail
 *
 * Every asset is addressed by the BLAKE3 of its bytes, and that is what makes
 * "two siblings scanning the same print pay for it once" true. It only works
 * if the browser computes the *same* hash the desktop app does, so this uses
 * BLAKE3 rather than the SHA-256 that WebCrypto would hand over for free.
 * Verified against the desktop library's own stored `content_hash`: identical,
 * byte for byte.
 *
 * ## Hashing before uploading, not instead of it
 *
 * The file is hashed first and offered second. A photograph the archive
 * already holds costs one round trip and no bytes, which on a re-import of a
 * folder somebody has added four pictures to is the difference between a
 * moment and a morning.
 *
 * ## What the server is told
 *
 * Dimensions and the capture date are read here, because the server has
 * neither a decoder nor the file. Faces are not: detection and embedding are
 * the client's job and this module deliberately does not do them, so an upload
 * never blocks on a 63 MB model. The face pass runs afterwards, against
 * whatever is already in the library.
 */

import { context2d, imageData, surface, toBlob } from "./canvas";
import { blake3 } from "hash-wasm";

/** Bytes we will accept. HEIC is listed but usually will not decode — see below. */
const IMAGE_TYPES = /^image\/(jpeg|png|webp|gif|avif|heic|heif|tiff)$/i;
const VIDEO_TYPES = /^video\/(mp4|quicktime|webm|x-m4v)$/i;

/** Refuse absurd files before reading them into memory. */
const MAX_BYTES = 512 * 1024 * 1024;

/**
 * How many files are in flight at once.
 *
 * Three rather than "as many as possible": each one holds its whole self in
 * memory to be hashed, and a tab that reads twenty 40-megapixel scans at once
 * is a tab that gets killed. Sequential would waste the link on small files.
 */
const CONCURRENCY = 3;

/**
 * How much of an EXIF date to believe.
 *
 * A scanned photograph's metadata is written by whoever digitised it, and the
 * shape of what they wrote is legible. The first of January at midnight is not
 * a day, it is a year somebody typed into a field that demanded a full date —
 * this archive is full of them. The Unix epoch is not even that: it is the
 * absence of a date, rendered as one.
 *
 * The desktop pipeline learned this the hard way and it is the same mistake to
 * make twice: claiming `day` here means the timeline renders "1 January 1970"
 * and asserts, in the interface, something nobody ever said.
 */
function precisionOf(iso: string): "day" | "year" | "unknown" {
  // 1970-01-01 in any timezone rendering of the epoch.
  if (iso.startsWith("1970-01-01")) return "unknown";
  const janFirst = /^\d{4}-01-01[ T]0?0?:?/.test(iso) || /^\d{4}-01-01/.test(iso);
  if (janFirst) return "year";
  return "day";
}

export interface Prepared {
  file: File;
  hash: string;
  /** A small JPEG for thumbnails. Null when nothing could decode the file. */
  proxy: Blob | null;
  kind: "image" | "video";
  width: number | null;
  height: number | null;
  capturedAt: string | null;
  capturedPrecision: "day" | "year" | "unknown";
  dateSource: "exif" | "mtime";
}

export interface UploadResult {
  added: number;
  deduplicated: number;
  failed: Array<{ name: string; why: string }>;
  assetIds: string[];
}

export interface UploadHooks {
  onProgress?(done: number, total: number, current: string): void;
  onFailure?(name: string, why: string): void;
}

/** Which of these files we are willing to take. */
export function acceptable(files: readonly File[]): {
  take: File[];
  skip: Array<{ name: string; why: string }>;
} {
  const take: File[] = [];
  const skip: Array<{ name: string; why: string }> = [];
  for (const f of files) {
    if (f.size === 0) {
      skip.push({ name: f.name, why: "the file is empty" });
    } else if (f.size > MAX_BYTES) {
      skip.push({ name: f.name, why: "larger than 512 MB" });
    } else if (IMAGE_TYPES.test(f.type) || VIDEO_TYPES.test(f.type)) {
      take.push(f);
    } else {
      skip.push({ name: f.name, why: f.type ? `${f.type} is not a photograph` : "unrecognised" });
    }
  }
  return { take, skip };
}

/**
 * Read everything about a file that the server cannot work out for itself.
 *
 * A file that will not decode still gets uploaded — it is somebody's
 * photograph and refusing it because this browser lacks a codec would lose it.
 * It arrives without dimensions, and the desktop app fills them in later. HEIC
 * is the common case: Safari decodes it, Chrome and Firefox do not.
 */
/**
 * Decode a photograph, whatever the browser thinks of its format.
 *
 * Native `createImageBitmap` first, because it is hardware-accelerated and
 * costs nothing. HEIC is the case that forces the fallback: measured here,
 * `createImageBitmap` on a HEIC file fails outright with "the source image
 * could not be decoded" in every browser but Safari — and the archive this was
 * built for is *971 of 971* HEIC. Without a decoder every thumbnail in it is a
 * broken-image icon.
 *
 * libheif is loaded lazily and only on that failure, because it is a couple of
 * megabytes of WebAssembly nobody with a folder of JPEGs should have to fetch.
 */
async function decode(file: File, bytes: Uint8Array): Promise<ImageData | null> {
  try {
    const bmp = await createImageBitmap(file);
    const s = surface(bmp.width, bmp.height);
    const ctx = context2d(s);
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    const out = imageData(s, bmp.width, bmp.height);
    bmp.close();
    return out;
  } catch {
    /* fall through */
  }
  if (!/heic|heif/i.test(file.type) && !/\.hei[cf]$/i.test(file.name)) return null;
  try {
    const { HeifDecoder } = await import("libheif-js");
    const images = new HeifDecoder().decode(bytes);
    const first = images?.[0];
    if (!first) return null;
    const w = first.get_width();
    const h = first.get_height();
    const data = new ImageData(w, h);
    await new Promise<void>((res, rej) =>
      first.display(data, (ok: unknown) => (ok ? res() : rej(new Error("decode failed")))),
    );
    return data;
  } catch {
    return null;
  }
}

/** Longest edge of the rendition thumbnails are drawn from. */
const PROXY_EDGE = 1280;

/**
 * A small JPEG of the same picture.
 *
 * The point is not tidiness. A grid showing a hundred photographs otherwise
 * downloads a hundred originals — 1.5 MB each here — and HEIC originals do not
 * render at all outside Safari. Quality 82 at 1280 px is a few tens of
 * kilobytes and indistinguishable at the size a thumbnail is actually seen.
 */
async function makeProxy(img: ImageData): Promise<Blob | null> {
  const scale = Math.min(1, PROXY_EDGE / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  try {
    const src = surface(img.width, img.height);
    context2d(src)?.putImageData(img, 0, 0);
    const dst = surface(w, h);
    const ctx = context2d(dst);
    if (!ctx) return null;
    ctx.drawImage(src.canvas as CanvasImageSource, 0, 0, w, h);
    return await toBlob(dst, "image/jpeg", 0.82);
  } catch {
    return null;
  }
}

export async function prepare(file: File): Promise<Prepared> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const hash = await blake3(bytes);
  const kind: "image" | "video" = VIDEO_TYPES.test(file.type) ? "video" : "image";

  let width: number | null = null;
  let height: number | null = null;
  let proxy: Blob | null = null;
  if (kind === "image") {
    // Decoded once and used twice: a file this big should not be turned into
    // pixels a second time just to read its dimensions.
    const img = await decode(file, bytes);
    if (img) {
      width = img.width;
      height = img.height;
      proxy = await makeProxy(img);
    }
  }

  const exif = kind === "image" ? exifDate(bytes) : null;
  return {
    file,
    hash,
    kind,
    proxy,
    width,
    height,
    // A file's modification time is when it was copied, not when the picture
    // was taken, so it is recorded as `unknown` precision rather than dressed
    // up as a date. That is what stops the archive filling with 1 January.
    capturedAt: exif ?? new Date(file.lastModified).toISOString().replace("T", " ").slice(0, 19),
    capturedPrecision: exif ? precisionOf(exif) : "unknown",
    dateSource: exif ? "exif" : "mtime",
  };
}

/**
 * Pull the capture date out of a file's EXIF.
 *
 * Worth the effort rather than leaving it to the server, which has neither the
 * file nor a decoder. Without it every photograph added from a browser is
 * dated to when the file was *copied*, and the archive is sorted by the wrong
 * thing from the first import — the exact failure the fuzzy-date work exists
 * to prevent.
 *
 * Two ways in, because one is not enough for a real archive:
 *
 *  1. **JPEG**, walked properly through the segment chain to APP1.
 *  2. **Anything else**, by scanning a bounded prefix for the `Exif\0\0`
 *     marker followed by a valid TIFF header.
 *
 * The second exists because of what people actually have. Measured on this
 * machine: the 971-photograph test archive is **entirely HEIC**, and a
 * JPEG-only reader found a date in none of it. HEIC keeps EXIF as an item
 * inside an ISO base-media container, and a full box parser is a great deal of
 * code to reach a payload that announces itself with a six-byte marker. The
 * scan is bounded so a large file cannot turn this into a linear search, and
 * it insists on the TIFF magic so it cannot latch onto the word "Exif"
 * appearing in, say, a comment.
 */
export function exifDate(bytes: Uint8Array): string | null {
  const at = jpegApp1(bytes) ?? findExifMarker(bytes);
  return at === null ? null : readTiffDate(bytes, at);
}

/** Offset of the TIFF header inside a JPEG's APP1 segment. */
function jpegApp1(bytes: Uint8Array): number | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let p = 2;
  while (p + 4 < bytes.length) {
    if (bytes[p] !== 0xff) return null;
    const marker = bytes[p + 1]!;
    if (marker === 0xda || marker === 0xd9) return null; // start of scan, or end
    const size = (bytes[p + 2]! << 8) | bytes[p + 3]!;
    if (size < 2) return null;
    if (
      marker === 0xe1 &&
      bytes[p + 4] === 0x45 && bytes[p + 5] === 0x78 &&
      bytes[p + 6] === 0x69 && bytes[p + 7] === 0x66
    ) {
      return p + 10; // past "Exif\0\0"
    }
    p += 2 + size;
  }
  return null;
}

/** How far in to look for an EXIF payload in a container we do not parse. */
const EXIF_SCAN_LIMIT = 1 << 20;

function findExifMarker(bytes: Uint8Array): number | null {
  const limit = Math.min(bytes.length - 8, EXIF_SCAN_LIMIT);
  for (let i = 0; i < limit; i++) {
    if (
      bytes[i] === 0x45 && bytes[i + 1] === 0x78 &&
      bytes[i + 2] === 0x69 && bytes[i + 3] === 0x66 &&
      bytes[i + 4] === 0x00 && bytes[i + 5] === 0x00
    ) {
      const tiff = i + 6;
      if (validTiff(bytes, tiff)) return tiff;
    }
  }
  return null;
}

function validTiff(bytes: Uint8Array, at: number): boolean {
  if (at + 8 > bytes.length) return false;
  const le = bytes[at] === 0x49 && bytes[at + 1] === 0x49;
  const be = bytes[at] === 0x4d && bytes[at + 1] === 0x4d;
  if (!le && !be) return false;
  const magic = le
    ? bytes[at + 2]! | (bytes[at + 3]! << 8)
    : (bytes[at + 2]! << 8) | bytes[at + 3]!;
  return magic === 0x002a;
}

/**
 * Read DateTimeOriginal from a TIFF header at `base`.
 *
 * IFD0 first for the sub-IFD pointer (tag 0x8769), then the sub-IFD for
 * DateTimeOriginal (0x9003) or DateTimeDigitized (0x9004), falling back to
 * IFD0's own DateTime (0x0132). Anything unexpected returns null rather than
 * guessing — a wrong date is worse than an honest absence, because an absence
 * is recorded as `unknown` precision and a wrong date is not.
 */
function readTiffDate(bytes: Uint8Array, base: number): string | null {
  if (!validTiff(bytes, base)) return null;
  const little = bytes[base] === 0x49;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u16 = (o: number) => (o + 2 <= bytes.length ? view.getUint16(o, little) : 0);
  const u32 = (o: number) => (o + 4 <= bytes.length ? view.getUint32(o, little) : 0);

  const ascii = (offset: number, count: number): string | null => {
    if (offset < 0 || offset + count > bytes.length) return null;
    let out = "";
    for (let i = 0; i < count; i++) {
      const c = bytes[offset + i]!;
      if (c === 0) break;
      out += String.fromCharCode(c);
    }
    return out;
  };

  // "2019:07:04 13:22:01" — EXIF spells the date half with colons.
  const parse = (s: string | null): string | null =>
    s && /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}/.test(s)
      ? `${s.slice(0, 4)}-${s.slice(5, 7)}-${s.slice(8, 10)} ${s.slice(11, 19)}`
      : null;

  const scan = (ifd: number, want: number[]): { date: string | null; sub: number | null } => {
    const n = u16(ifd);
    // A plausible IFD has a handful of entries, not sixty thousand; a wild
    // count means this is not really an IFD.
    if (n === 0 || n > 512) return { date: null, sub: null };
    let date: string | null = null;
    let sub: number | null = null;
    for (let i = 0; i < n; i++) {
      const entry = ifd + 2 + i * 12;
      if (entry + 12 > bytes.length) break;
      const tag = u16(entry);
      if (tag === 0x8769) sub = base + u32(entry + 8);
      else if (want.includes(tag) && !date) {
        const count = u32(entry + 4);
        if (count >= 19) date = parse(ascii(base + u32(entry + 8), Math.min(count, 20)));
      }
    }
    return { date, sub };
  };

  const first = scan(base + u32(base + 4), [0x0132]);
  if (first.sub !== null) {
    const sub = scan(first.sub, [0x9003, 0x9004]);
    if (sub.date) return sub.date;
  }
  return first.date;
}

/**
 * Upload prepared files and register them.
 *
 * One file failing costs that file. A folder of four hundred photographs where
 * one is a corrupt scan should end with three hundred and ninety-nine in the
 * archive and one named in the report, not with an error and nothing done.
 */
export async function uploadAll(
  libraryId: string,
  files: readonly File[],
  hooks: UploadHooks = {},
): Promise<UploadResult> {
  const { take, skip } = acceptable(files);
  const result: UploadResult = {
    added: 0,
    deduplicated: 0,
    failed: [...skip],
    assetIds: [],
  };
  for (const s of skip) hooks.onFailure?.(s.name, s.why);
  if (!take.length) return result;

  // Hash everything first, then ask in one round trip what is already here.
  // Hashing is local and fast; it is the uploading that is worth avoiding.
  const held = new Set<string>();
  {
    const digests: string[] = [];
    for (let i = 0; i < take.length; i++) {
      hooks.onProgress?.(i, take.length, `checking ${take[i]!.name}`);
      try {
        digests.push(await blake3(new Uint8Array(await take[i]!.arrayBuffer())));
      } catch {
        /* unreadable here means unreadable later; the worker reports it */
      }
    }
    for (let i = 0; i < digests.length; i += 500) {
      try {
        const res = await fetch(`/api/lib/${libraryId}/have`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hashes: digests.slice(i, i + 500) }),
        });
        if (res.ok) for (const h of ((await res.json()) as { have: string[] }).have) held.add(h);
      } catch {
        // Not knowing means uploading, which is correct but slower.
      }
    }
  }

  let cursor = 0;
  let done = 0;
  // Hashes this run has already sent, so two copies of the same photograph in
  // one drop upload once rather than racing each other.
  const sent = new Set<string>();

  const worker = async (): Promise<void> => {
    for (;;) {
      const index = cursor++;
      const file = take[index];
      if (!file) return;
      hooks.onProgress?.(done, take.length, file.name);
      try {
        const p = await prepare(file);

        // Ask before sending. The server would dedupe anyway, but only after
        // receiving every byte — which on a folder that is mostly already here
        // is the whole import.
        const known = held.has(p.hash) || sent.has(p.hash);
        sent.add(p.hash);

        let key = `blobs/${p.hash}`;
        if (!known) {
          const up = await fetch(`/api/lib/${libraryId}/upload/${p.hash}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": file.type || "application/octet-stream" },
            body: file,
          });
          if (!up.ok) throw new Error(await reason(up));
          key = ((await up.json()) as { key: string }).key;
        }
        const deduplicated = known;

        // The rendition thumbnails are drawn from. Its own content hash, so
        // two libraries holding the same photograph share one proxy too.
        let proxyKey: string | null = null;
        if (p.proxy) {
          const proxyHash = await blake3(new Uint8Array(await p.proxy.arrayBuffer()));
          const pr = await fetch(`/api/lib/${libraryId}/upload/${proxyHash}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "image/jpeg" },
            body: p.proxy,
          });
          // A failed proxy is a slow thumbnail, not a lost photograph.
          if (pr.ok) proxyKey = ((await pr.json()) as { key: string }).key;
        }

        const id = crypto.randomUUID();
        const reg = await fetch(`/api/lib/${libraryId}/assets`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            asset: {
              id,
              contentHash: p.hash,
              fileName: file.name,
              ext: (file.name.split(".").pop() ?? "").toLowerCase(),
              byteSize: file.size,
              kind: p.kind,
              width: p.width,
              height: p.height,
              capturedAt: p.capturedAt,
              capturedPrecision: p.capturedPrecision,
              dateSource: p.dateSource,
              blobKey: key,
              proxyKey,
            },
            faces: [],
          }),
        });
        if (!reg.ok) throw new Error(await reason(reg));

        result.assetIds.push(id);
        if (deduplicated) result.deduplicated++;
        else result.added++;
      } catch (e) {
        const why = e instanceof Error ? e.message : "could not be added";
        result.failed.push({ name: file.name, why });
        hooks.onFailure?.(file.name, why);
      } finally {
        done++;
        hooks.onProgress?.(done, take.length, file.name);
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, take.length) }, () => worker()),
  );
  return result;
}

/** The server's own words when it refuses, rather than a status code. */
async function reason(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    /* not JSON */
  }
  return res.status === 507 ? "this archive is full" : `refused (${res.status})`;
}

/**
 * Walk a dropped folder.
 *
 * A drop of a folder gives entries, not files, and dropping the folder is what
 * people actually do with a scanned archive. Depth is capped because a
 * symlinked loop is otherwise an infinite walk.
 */
export async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const entries = [...dt.items]
    .map((i) => (i.kind === "file" ? i.webkitGetAsEntry?.() ?? null : null))
    .filter((e): e is FileSystemEntry => e !== null);

  if (!entries.length) return [...dt.files];

  const out: File[] = [];
  const walk = async (entry: FileSystemEntry, depth: number): Promise<void> => {
    if (depth > 8 || out.length > 5000) return;
    if (entry.isFile) {
      const file = await new Promise<File | null>((res) =>
        (entry as FileSystemFileEntry).file(res, () => res(null)),
      );
      if (file) out.push(file);
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((res) =>
        reader.readEntries(res, () => res([])),
      );
      if (!batch.length) break;
      for (const child of batch) await walk(child, depth + 1);
    }
  };
  for (const e of entries) await walk(e, 0);
  return out;
}
